const TEN_MINUTES_IN_MS = 10 * 60 * 1000

export type GetCurrentTimeFn = () => number
const getCurrentTime = () => {
  return Date.now()
}

/**
 * A BackoffSafeguard class which stops a function from being called too frequently.
 */
export class BackoffSafeguard {
  private count: number
  private timeWindow: number // in milliseconds
  private timestamps: number[]
  private getCurrentTime: GetCurrentTimeFn

  /**
   * @param count The maximum number of times the function can be called within the time window.
   * @param timeWindow The time window in milliseconds during which the function can be called a maximum of `count` times.
   */
  constructor(count = 10, timeWindow = TEN_MINUTES_IN_MS) {
    this.count = count
    this.timeWindow = timeWindow
    this.timestamps = []
    this.getCurrentTime = getCurrentTime
  }

  /**
   * Clears the timestamps outside of the time window and checks if there's been too many calls already.
   * @returns `true` if the function can be called, `false` if it has been called too many times within the time window.
   */
  isAllowed(): boolean {
    // Filter timestamps to only keep those within the time window
    const now = this.getCurrentTime()
    this.timestamps = this.timestamps.filter(ts => now - ts < this.timeWindow)
    // Check if the number of timestamps is less than the count limit
    return this.timestamps.length < this.count
  }

  /**
   * Calls the provided function if it is allowed to be called, after adding the current time to the timestamps.
   * @param fn The function to call if allowed.
   */
  callIfAllowed(fn: () => Promise<void>): Promise<void> {
    if (this.isAllowed()) {
      this.timestamps.push(this.getCurrentTime())
      return fn()
    } else {
      return Promise.resolve()
    }
  }

  /**
   * Set the current time function used by the safeguard.
   * Used for backing up the current function, which can be useful for testing or mocking.
   */
  setCurrentTimeFn(fn: GetCurrentTimeFn): void {
    this.getCurrentTime = fn
  }
}
